"use client";

import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Link } from 'react-router-dom';
import { ArrowLeft, FileText, Shield, DollarSign, Users, Zap, Clock, Building, Globe, AlertTriangle, Download, Mail } from 'lucide-react';

const Terms = () => {
  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-slate-50 to-zinc-50 relative overflow-hidden">
      {/* Minimal Subtle Patterns */}
      <div className="absolute inset-0 opacity-5 pointer-events-none">
        <div className="absolute top-0 left-0 w-full h-full bg-[url('data:image/svg+xml,%3Csvg width=%2240%22 height=%2240%22 viewBox=%220 0 40 40%22 xmlns=%22http://www.w3.org/2000/svg%22%3E%3Cpath d=%22M0 20 L40 20 M20 0 L20 40%22 stroke=%22%23f1f5f9%22 stroke-width=%220.5%22/%3E%3C/svg%3E')]"></div>
        <div className="absolute top-1/2 left-1/2 w-96 h-96 bg-slate-100/20 rounded-full blur-3xl"></div>
      </div>

      <div className="container max-w-4xl mx-auto px-4 py-12 relative z-10">
        <Button variant="ghost" asChild className="mb-8">
          <Link to="/">
            <ArrowLeft className="mr-2 h-4 w-4" />
            Back to Home
          </Link>
        </Button>

        <Card>
          <CardHeader className="flex items-center space-x-2">
            <FileText className="h-5 w-5" />
            <CardTitle>Terms of Service</CardTitle>
          </CardHeader>
          <CardContent className="space-y-6 prose max-w-none">
            <section>
              <h2 className="flex items-center space-x-2 mb-2">
                <Shield className="h-5 w-5 text-blue-500" />
                <span>1. Acceptance of Terms</span>
              </h2>
              <p>By accessing and using WightWay Connect (the "Service"), you accept and agree to be bound by the terms and provision of this Terms of Service Agreement ("Agreement"). If you do not agree with these terms, please do not use the Service.</p>
              <p>This Agreement is entered into between you ("User," "you," or "your") and WightWay, a brand of LPIWuk LTD (Company No. 12196636), located at Building 41, Newport Road, Cowes, United Kingdom, PO31 8BX ("Company," "we," "us," or "our").</p>
            </section>

            <section>
              <h2 className="flex items-center space-x-2 mb-2">
                <Zap className="h-5 w-5 text-green-500" />
                <span>2. Description of Service</span>
              </h2>
              <p>The Service provides a secure client portal platform for businesses and teams to manage projects, track time, handle invoicing, and facilitate collaboration. The Service may include additional features and functionalities as described on our website.</p>
            </section>

            <section>
              <h2 className="flex items-center space-x-2 mb-2">
                <Users className="h-5 w-5 text-purple-500" />
                <span>3. User Accounts</span>
              </h2>
              <p>In order to use the Service, you must register for an account ("Account") and provide certain information about yourself as prompted by the registration form. You represent and warrant that all information you provide is accurate, complete, and current.</p>
              <p>You are responsible for maintaining the confidentiality of your Account login information and for all activities that occur under your Account. You agree to immediately notify us of any unauthorized use of your Account.</p>
            </section>

            <section>
              <h2 className="flex items-center space-x-2 mb-2">
                <Download className="h-5 w-5 text-indigo-500" />
                <span>4. Use of Service</span>
              </h2>
              <p>You agree to use the Service only for purposes that are permitted by this Agreement and any applicable law, regulation, or generally accepted practices or guidelines in the applicable jurisdiction.</p>
              <p>You are responsible for your use of the Service and for any Content you provide. You agree not to use the Service to:</p>
              <ul>
                <li>Violate any applicable law or regulation;</li>
                <li>Infringe the rights of any third party;</li>
                <li>Upload or transmit any malicious code or harmful material;</li>
                <li>Interfere with or disrupt the Service or servers;</li>
                <li>Attempt to gain unauthorized access to the Service.</li>
              </ul>
            </section>

            <section>
              <h2 className="flex items-center space-x-2 mb-2">
                <DollarSign className="h-5 w-5 text-yellow-500" />
                <span>5. Payments and Billing</span>
              </h2>
              <p>Our Service may require payment of fees for certain features. All fees are non-refundable except as expressly provided in this Agreement. You agree to pay all fees associated with your use of the Service.</p>
              <p>We may change our fees at any time. We will provide reasonable notice of any fee changes.</p>
              <p>We offer a 7-day money back guarantee on all paid plans. You may cancel your subscription and receive a full refund within 7 days of your initial payment. To request a refund, contact support@wightwayconnect.uk within 7 days of purchase. Refunds are processed within 5-10 business days.</p>
            </section>

            <section>
              <h2 className="flex items-center space-x-2 mb-2">
                <Mail className="h-5 w-5 text-blue-500" />
                <span>6. Contact Us</span>
              </h2>
              <p>If you have questions about this Terms of Service, please contact our team:</p>
              <ul>
                <li>Email: support@wightwayconnect.uk</li>
                <li>Address: LPIWuk LTD, Building 41, Newport Road, Cowes,  United Kingdom, PO31 8BX</li>
              </ul>
              <p className="text-sm text-muted-foreground">Last updated: October 2025</p>
            </section>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default Terms;